import { getLogger as baseGetLogger } from '@logtape/logtape';

import { isSensitiveKey } from '@/logger/redaction/redact';

export type PlainValue
  = | string
    | number
    | boolean
    | null
    | undefined
    | Date
    | readonly PlainValue[]
    | { [key: string]: PlainValue };

const NANOSECONDS_PER_MILLISECOND = 1_000_000;

export function toNanoseconds(milliseconds: number): number {
  return Math.round(milliseconds * NANOSECONDS_PER_MILLISECOND);
}

type StandardAttributes = {
  error?: unknown;
  duration?: number;
  http?: { method?: string; status_code?: number; url?: string };
  user?: { id: string };
};

type LogMeta<Meta> = {
  [Key in keyof Meta]: Key extends keyof StandardAttributes ? StandardAttributes[Key] : PlainValue;
};

const PLATFORM_OWNED_NAMES: ReadonlySet<string> = new Set([
  'context', 'env', 'level', 'message', 'request_id', 'service', 'session_id',
  'span_id', 'timestamp', 'trace_id', 'version',
  'date', 'logger', 'origin', 'session', 'status', 'usr', 'view',
]);

const STANDARD_ATTRIBUTE_FIELDS: ReadonlyMap<string, ReadonlySet<string>> = new Map([
  ['http', new Set(['method', 'status_code', 'url'])],
  ['user', new Set(['id'])],
]);

const MAX_ASSERT_DEPTH = 8;

function findReservedNames(meta: Record<string, unknown>): string[] {
  return Object.keys(meta).filter((key) => PLATFORM_OWNED_NAMES.has(key));
}

function stripPlatformOwnedNames(meta: Record<string, unknown>): Record<string, unknown> {
  const domain: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(meta)) {
    if (!PLATFORM_OWNED_NAMES.has(key)) {
      domain[key] = value;
    }
  }

  return domain;
}

function isPlainData(value: unknown): boolean {
  if (value === null || value instanceof Date || Array.isArray(value)) {
    return true;
  }

  if (typeof value === 'function' || typeof value === 'symbol') {
    return false;
  }

  if (typeof value !== 'object') {
    return true;
  }

  const prototype = Object.getPrototypeOf(value);

  return prototype === Object.prototype || prototype === null;
}

function assertPlainData(value: unknown, path: string, depth: number, seen: WeakSet<object>): void {
  if (!isPlainData(value)) {
    throw new Error(
      `Log meta must be plain data: ${path || 'meta'} carries a ${value?.constructor?.name ?? typeof value}. `
      + 'Enumerate the fields the record needs, or hand the cause to the error slot.',
    );
  }

  if (typeof value !== 'object' || value === null || depth > MAX_ASSERT_DEPTH || seen.has(value)) {
    return;
  }

  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item) => assertPlainData(item, path, depth + 1, seen));

    return;
  }

  for (const [key, nested] of Object.entries(value)) {
    const keyPath = path ? `${path}.${key}` : key;

    if (isSensitiveKey(key)) {
      throw new Error(
        `Log meta may not carry personal data: ${keyPath} matches the sensitive-key list. `
        + 'Identify the person by user.id and the case by an opaque id.',
      );
    }

    assertPlainData(nested, keyPath, depth + 1, seen);
  }
}

function assertContractShape(meta: Record<string, unknown>): void {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  const reserved = findReservedNames(meta);

  if (reserved.length > 0) {
    throw new Error(
      `Log meta may not carry reserved top-level names: ${reserved.join(', ')}. `
      + 'Move them into an object named after the domain.',
    );
  }

  if ('duration' in meta && typeof meta.duration !== 'number') {
    throw new Error('Log meta duration must be a number of nanoseconds.');
  }

  for (const [attribute, allowedFields] of STANDARD_ATTRIBUTE_FIELDS) {
    const value = meta[attribute];

    if (value === undefined || value === null) {
      continue;
    }

    const extraFields = Object.keys(value).filter((field) => !allowedFields.has(field));

    if (extraFields.length > 0) {
      throw new Error(
        `Log meta ${attribute} may only carry ${[...allowedFields].join(', ')} — got ${extraFields.join(', ')}.`,
      );
    }
  }

  assertPlainData(meta, '', 0, new WeakSet());
}

function serializeError(error: unknown) {
  if (error instanceof Error) {
    return { kind: error.name, message: error.message, stack: error.stack };
  }

  return { kind: 'NonErrorThrown', message: String(error) };
}

function buildProperties(meta?: unknown): Record<string, unknown> {
  const metaIsError = meta instanceof Error;
  const isObject = typeof meta === 'object' && meta !== null && !metaIsError;
  const declared: Record<string, unknown> = isObject
    ? { ...(meta as Record<string, unknown>) }
    : {};
  const errorValue = metaIsError ? meta : declared.error;

  delete declared.error;
  assertContractShape(declared);

  const properties = stripPlatformOwnedNames(declared);

  if (errorValue !== undefined) {
    properties.error = serializeError(errorValue);
  }

  return properties;
}

export function getLogger(context?: string) {
  const logger = baseGetLogger(context ? ['app', context] : ['app']);

  return {
    error<Meta extends Record<string, unknown>>(message: string, meta?: Meta & LogMeta<Meta>) {
      logger.error(message, buildProperties(meta));
    },

    info<Meta extends Record<string, unknown>>(message: string, meta?: Meta & LogMeta<Meta>) {
      logger.info(message, buildProperties(meta));
    },

    warn<Meta extends Record<string, unknown>>(message: string, meta?: Meta & LogMeta<Meta>) {
      logger.warn(message, buildProperties(meta));
    },

    debug<Meta extends Record<string, unknown>>(message: string, meta?: Meta & LogMeta<Meta>) {
      logger.debug(message, buildProperties(meta));
    },
  };
}
